import { buildPrompt } from "../messages/prompt.js";
import { startOpencodeSession } from "../process/server.js";
import { startOpencodeServerInSandbox } from "../process/sandbox-server.js";
import { resolveInteractivePermission } from "../process/permissions.js";
import { AsyncQueue } from "../stream/queue.js";
import { translateOpencodeStream } from "../stream/translate.js";
import { projectOpencodeWorkspace } from "./projection.js";
import { EventType, normalizeSystemPrompts } from "@tanstack/ai";
import { toRunErrorRawEvent } from "@tanstack/ai/adapter-internals";
import { BaseTextAdapter } from "@tanstack/ai/adapters";
import { DurableAttachNotSupportedError, SandboxCapability, buildApprovalRequestedEvent, createBridgeEventChannel, getSandbox, getSandboxDurability, getToolBridgeProvisioner, getWorkspaceProjection, mergeChunkStreams, nodeHttpBridgeProvisioner, resolveDurableRunId } from "@tanstack/ai-sandbox";
//#region src/adapters/text.ts
var DEFAULT_WORKDIR = "/workspace";
var DEFAULT_PORT = 4096;
/** Split a `provider/model` id into its provider and model halves. */
function splitModel(model) {
	const slash = model.indexOf("/");
	if (slash <= 0 || slash === model.length - 1) throw new Error(`OpenCode models must be addressed as "provider/model" (e.g. "anthropic/claude-sonnet-4-5"); received "${model}".`);
	return {
		providerID: model.slice(0, slash),
		modelID: model.slice(slash + 1)
	};
}
var OpencodeTextAdapter = class extends BaseTextAdapter {
	name = "opencode";
	requires = [SandboxCapability];
	adapterConfig;
	constructor(config, model) {
		super({}, model);
		this.adapterConfig = config;
	}
	sandboxFrom(options) {
		const ctx = options.capabilities;
		if (!ctx) throw new Error("Adapter \"opencode\" requires a sandbox. Add withSandbox(defineSandbox({ ... })) to chat() middleware.");
		return getSandbox(ctx);
	}
	applySystemPrompts(options, prompt) {
		const systemPrompts = normalizeSystemPrompts(options.systemPrompts).map((systemPrompt) => systemPrompt.content).filter((content) => content.trim() !== "");
		if (systemPrompts.length === 0) return prompt;
		return `${systemPrompts.join("\n\n")}\n\n${prompt}`;
	}
	async *chatStream(options) {
		const { logger } = options;
		let server;
		let handle;
		let bridge;
		const externalSignal = options.abortController?.signal ?? options.request?.signal ?? void 0;
		let onAbort;
		const runId = resolveDurableRunId(options.runId, {
			durable: false,
			adapter: "opencode",
			fallback: () => this.generateId()
		});
		const threadId = options.threadId ?? this.generateId();
		const channel = createBridgeEventChannel({
			model: this.model,
			threadId,
			runId
		});
		try {
			const sandbox = this.sandboxFrom(options);
			const directory = options.modelOptions?.directory ?? this.adapterConfig.directory ?? DEFAULT_WORKDIR;
			const durability = options.capabilities ? getSandboxDurability(options.capabilities, { optional: true }) : void 0;
			if (durability !== void 0) {
				if (durability.attach) throw new DurableAttachNotSupportedError("opencode", "this adapter drives the harness over the opencode HTTP server and does not journal");
				logger.warn("opencode: sandbox durability is wired but this adapter never journals — this run will not be recoverable on reconnect. Use a journaling harness adapter for runs that must survive a host restart, or drop durability if these runs are not meant to.", {
					runId,
					adapter: "opencode"
				});
			}
			if (options.capabilities !== void 0) {
				const projection = getWorkspaceProjection(options.capabilities, { optional: true });
				if (projection !== void 0) await projectOpencodeWorkspace(sandbox, projection);
			}
			const modelOptions = options.modelOptions;
			const sessionId = modelOptions?.sessionId;
			const { prompt: resumePrompt } = buildPrompt(options.messages, sessionId);
			const { providerID, modelID } = splitModel(this.model);
			const bridgedToolNames = new Set((options.tools ?? []).map((tool) => tool.name));
			if (options.tools && options.tools.length > 0) bridge = await ((options.capabilities ? getToolBridgeProvisioner(options.capabilities, { optional: true }) : void 0) ?? nodeHttpBridgeProvisioner).provision(options.tools, {
				provider: sandbox.provider,
				context: options.context,
				emitCustomEvent: channel.emitCustomEvent,
				...externalSignal ? { signal: externalSignal } : {}
			});
			const approvalRequests = [];
			const queue = new AsyncQueue();
			const mode = modelOptions?.permissionMode ?? this.adapterConfig.permissionMode ?? "default";
			const permissionHandler = this.adapterConfig.onPermissionRequest ?? ((request) => {
				const result = resolveInteractivePermission(request, mode, bridgedToolNames, options.approvals);
				if (result.approvalId !== void 0) approvalRequests.push(buildApprovalRequestedEvent({
					approvalId: result.approvalId,
					title: result.title ?? request.title,
					threadId,
					runId,
					detail: { provider: "opencode" }
				}));
				return result.response;
			});
			logger.request(`activity=chat provider=opencode model=${this.model} sandbox=${sandbox.provider} messages=${options.messages.length} resume=${sessionId ?? "none"}`, {
				provider: "opencode",
				model: this.model
			});
			const serverEnv = bridge ? { OPENCODE_CONFIG_CONTENT: JSON.stringify({ mcp: { [bridge.name]: {
				type: "remote",
				url: bridge.url,
				enabled: true,
				headers: { Authorization: `Bearer ${bridge.token}` }
			} } }) } : void 0;
			server = await startOpencodeServerInSandbox(sandbox, {
				port: this.adapterConfig.port ?? DEFAULT_PORT,
				...this.adapterConfig.hostname !== void 0 && { hostname: this.adapterConfig.hostname },
				cwd: directory,
				...serverEnv ? { env: serverEnv } : {},
				...externalSignal ? { signal: externalSignal } : {}
			});
			handle = await startOpencodeSession({
				baseUrl: server.baseUrl,
				...server.headers !== void 0 && { headers: server.headers },
				providerID,
				modelID,
				...sessionId !== void 0 && { resumeSessionId: sessionId },
				onEvent: (event) => queue.push({
					kind: "event",
					event
				}),
				onPermissionRequest: permissionHandler,
				onError: (error) => queue.fail(error)
			});
			const session = handle;
			if (externalSignal !== void 0) {
				onAbort = () => void session.abort().catch(() => void 0);
				if (externalSignal.aborted) onAbort();
				else externalSignal.addEventListener("abort", onAbort, { once: true });
			}
			queue.push({
				kind: "session",
				sessionId: session.sessionId
			});
			const promptText = this.applySystemPrompts(options, session.resumed || sessionId === void 0 ? resumePrompt : buildPrompt(options.messages, void 0).prompt);
			session.prompt(promptText).then(({ message }) => {
				queue.push({
					kind: "done",
					message
				});
				queue.end();
			}).catch((error) => queue.fail(error));
			yield* mergeChunkStreams(translateOpencodeStream(queue, {
				model: this.model,
				runId,
				threadId,
				...options.parentRunId !== void 0 && { parentRunId: options.parentRunId },
				genId: () => this.generateId(),
				bridgedToolNames,
				onStreamEvent: (event) => logger.provider(`provider=opencode kind=${event.kind}`, { chunk: event })
			}), channel.stream);
			for (const event of approvalRequests) yield event;
		} catch (error) {
			const err = error;
			const rawEvent = toRunErrorRawEvent(error);
			logger.errors("opencode.chatStream fatal", {
				error,
				source: "opencode.chatStream"
			});
			yield {
				type: EventType.RUN_ERROR,
				model: options.model,
				timestamp: Date.now(),
				message: err.message || "Unknown error occurred",
				...err.code !== void 0 && { code: err.code },
				...rawEvent !== void 0 && { rawEvent },
				error: {
					message: err.message || "Unknown error occurred",
					...err.code !== void 0 && { code: err.code }
				}
			};
		} finally {
			if (externalSignal !== void 0 && onAbort !== void 0) externalSignal.removeEventListener("abort", onAbort);
			channel.close();
			await handle?.dispose();
			await server?.dispose();
			await bridge?.close();
		}
	}
	structuredOutput(_options) {
		return Promise.reject(/* @__PURE__ */ new Error("Structured output is not yet supported by the in-sandbox OpenCode adapter. Use a model adapter for structured output, or omit outputSchema."));
	}
};
/**
* Creates an OpenCode harness adapter that runs **inside a sandbox**.
*
* It declares `requires: [SandboxCapability]`, spawns `opencode serve` inside
* the sandbox provided by `withSandbox(...)`, exposes its port, and connects
* the `@opencode-ai/sdk` HTTP client to it. OpenCode owns the agent loop and
* executes its native tools against the sandbox workspace. The sandbox image
* must provide the `opencode` executable (Docker: also publish the server port
* via `publishPorts`). chat()-provided tools aren't bridged yet.
*/
function opencodeText(model, config = {}) {
	return new OpencodeTextAdapter(config, model);
}
//#endregion
export { OpencodeTextAdapter, opencodeText };

//# sourceMappingURL=text.js.map